import { useEffect } from "react";
import "./App.css";

// PublishAfrica — Clean URL rendering.
// In production (publish-africa.com), the deployed server serves the React shell
// for every unknown path (SPA fallback). To keep the browser URL clean
// (e.g. /awards instead of /publishafrica-awards.html), we DO NOT redirect:
// we fetch() the target HTML and rewrite the document while keeping the
// pristine URL via history.replaceState.
//
// Special routes:
//   - /admin<DDMMYY>  — date-rotating admin gate
//   - /monetisation, /validation — deprecated, must 404

// Clean URL (exact) → static HTML file
const CLEAN_URL_MAP = {
  "/": "/publishafrica-v3.html",
  "/accueil": "/publishafrica-v3.html",
  "/journal": "/publishafrica-journal.html",
  "/recherche": "/publishafrica-search.html",
  "/tendances": "/publishafrica-trending.html",
  "/awards": "/publishafrica-awards.html",
  "/academy": "/publishafrica-academy.html",
  "/tarifs": "/publishafrica-pricing.html",
  "/inscription": "/publishafrica-register.html",
  "/connexion": "/publishafrica-login.html",
  "/dashboard": "/publishafrica-dashboard.html",
  "/editeur": "/publishafrica-editor.html",
  "/profil": "/publishafrica-profile.html",
  "/presse": "/publishafrica-press.html",
  "/entreprises": "/publishafrica-corporate.html",
  "/a-propos": "/publishafrica-about.html",
  "/legal": "/publishafrica-legal.html",
  "/engagement": "/publishafrica-engagement.html",
  "/rubriques": "/publishafrica-rubriques.html",
  "/monetisation": "/publishafrica-monetisation.html",
  "/validation": "/publishafrica-validation.html",
  "/admin/seo": "/publishafrica-admin-seo.html",
};

const CLEAN_PREFIX_MAP = [
  { test: /^\/article(\/|$)/, file: "/publishafrica-article.html", param: "slug" },
  { test: /^\/auteur(\/|$)/, file: "/publishafrica-author.html", param: "handle" },
];

const DEPRECATED_PATHS = new Set([]);

// Guard against StrictMode double-invocation of useEffect (dev only).
// In production this stays false and renderInto runs once per real navigation.
let __PA_RENDERED__ = false;

// Render a target HTML file into the current document WITHOUT changing the URL.
// Strategy: use document.open()/write()/close() — the browser natively re-parses
// the HTML, executes scripts in order, and fires DOMContentLoaded as normal.
// We use history.replaceState() FIRST so the new document inherits the clean URL.
async function renderInto(targetUrl, displayUrl) {
  if (__PA_RENDERED__) return false;
  __PA_RENDERED__ = true;
  try {
    const res = await fetch(targetUrl, { credentials: "same-origin" });
    if (!res.ok) throw new Error("HTTP " + res.status);
    const html = await res.text();

    // Lock the clean URL into the address bar before re-parsing the document.
    try {
      window.history.replaceState(null, "", displayUrl);
    } catch (_) {
      /* ignore */
    }

    // Replace the whole document. The browser will re-parse, execute scripts in
    // declaration order, and fire DOMContentLoaded — exactly as if the user had
    // loaded targetUrl directly, but the URL bar stays at displayUrl.
    document.open();
    document.write(html);
    document.close();
    return true;
  } catch (e) {
    console.error("[clean-url-render]", e);
    // Fallback to a hard redirect so the user still sees content.
    window.location.replace(targetUrl);
    return false;
  }
}

function App() {
  useEffect(() => {
    const pathname = window.location.pathname || "/";
    const search = window.location.search || "";
    const hash = window.location.hash || "";

    // If the user landed directly on a /publishafrica-*.html (legacy URL),
    // 301-style redirect them to the canonical clean URL.
    if (pathname.startsWith("/publishafrica-") && pathname.endsWith(".html")) {
      // Find clean URL by reverse-lookup
      let cleanUrl = null;
      for (const [clean, html] of Object.entries(CLEAN_URL_MAP)) {
        if (html === pathname) {
          cleanUrl = clean;
          break;
        }
      }
      // Check prefix map (article/auteur)
      if (!cleanUrl) {
        for (const entry of CLEAN_PREFIX_MAP) {
          if (entry.file === pathname) {
            cleanUrl = entry.test.source.match(/^\\\/(\w+)/);
            cleanUrl = cleanUrl ? "/" + cleanUrl[1] : null;
            break;
          }
        }
      }
      if (cleanUrl) {
        // Render the page in place but expose the clean URL
        renderInto(pathname, cleanUrl + search + hash);
        return;
      }
      // Otherwise let the browser load the .html natively (no React app)
      return;
    }

    // 1) Deprecated routes — show 404
    if (DEPRECATED_PATHS.has(pathname)) {
      renderInto("/publishafrica-404.html", "/publishafrica-404.html");
      return;
    }

    // 2) Admin date-gate
    const adminMatch = pathname.match(/^\/admin(\d{6})\/?$/);
    if (adminMatch) {
      const provided = adminMatch[1];
      const pad = (n) => String(n).padStart(2, "0");
      const fmt = (d) =>
        pad(d.getDate()) + pad(d.getMonth() + 1) + String(d.getFullYear()).slice(-2);
      const today = new Date();
      const yesterday = new Date(today.getTime() - 24 * 3600 * 1000);
      const tomorrow = new Date(today.getTime() + 24 * 3600 * 1000);
      const allowed = new Set([fmt(today), fmt(yesterday), fmt(tomorrow)]);
      if (allowed.has(provided)) {
        try {
          sessionStorage.setItem("pa_admin_gate", "1");
        } catch (_) {
          /* ignore */
        }
        renderInto("/publishafrica-admin.html", pathname);
        return;
      }
      renderInto("/publishafrica-404.html", "/publishafrica-404.html");
      return;
    }
    if (pathname === "/admin" || pathname === "/admin/") {
      renderInto("/publishafrica-404.html", "/publishafrica-404.html");
      return;
    }

    // 3) Clean URL exact match → render in place
    if (Object.prototype.hasOwnProperty.call(CLEAN_URL_MAP, pathname)) {
      renderInto(CLEAN_URL_MAP[pathname], pathname + search + hash);
      return;
    }

    // 4) Prefix match (/article/slug-here → publishafrica-article.html?slug=…)
    for (const entry of CLEAN_PREFIX_MAP) {
      const m = pathname.match(entry.test);
      if (m) {
        const tail = pathname.replace(entry.test, "").replace(/^\/+/, "");
        let qs = search;
        if (tail) {
          qs = (search ? search + "&" : "?") + entry.param + "=" + encodeURIComponent(tail);
        }
        renderInto(entry.file + qs + hash, pathname + search + hash);
        return;
      }
    }

    // 5) Unknown single-segment path → maybe an article slug?
    // Pattern: /any-kebab-case-string (no further slashes)
    const slugMatch = pathname.match(/^\/([a-z0-9][a-z0-9-]{1,100})\/?$/);
    if (slugMatch) {
      const slug = slugMatch[1];
      // Try /api/articles/by-slug/<slug> — async, fall back to 404 on miss
      fetch("/api/articles/by-slug/" + encodeURIComponent(slug), {
        credentials: "same-origin",
      })
        .then((r) => {
          if (r.ok) {
            // Render the article page with the slug as query param. The
            // browser URL stays the clean /<slug> form.
            renderInto(
              "/publishafrica-article.html?slug=" + encodeURIComponent(slug),
              "/" + slug + search + hash,
            );
          } else {
            renderInto("/publishafrica-404.html", pathname);
          }
        })
        .catch(() => {
          renderInto("/publishafrica-404.html", pathname);
        });
      return;
    }

    // 6) Truly unknown route → 404
    renderInto("/publishafrica-404.html", pathname);
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#0F2260",
        color: "#FDF8EE",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <p style={{ fontSize: 14, letterSpacing: 1 }}>Chargement de PublishAfrica…</p>
    </div>
  );
}

export default App;
